import { Link } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../context/AuthProvider";
import { BASE_URL } from '../apiConfig';
import { toast } from 'sonner';

export default function DeliveryCompanyCard({ company, fetchCompanies }) {
  const { token } = useAuth();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    client_id: company.client_id || '',
    token_api: company.token_api || '',
    key_api: company.key_api || '',
    status: company.status || 'false'
  });
  const [connectData, setConnectData] = useState({
    client_id: '',
    key_api: '',
    token_api: ''
  });

  const handleEditSubmit = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/delivery-companies/${company.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (result.success) {
        setIsEditModalOpen(false);
        fetchCompanies();
        toast.success('Company updated successfully!');
      } else {
        throw new Error(result.message || 'Failed to update company');
      }
    } catch (error) {
      console.error('Error updating company:', error);
      toast.error(error.message || 'Failed to update company');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnectSubmit = async () => {
    setIsLoading(true);
    try {
    
      
      const response = await fetch(`${BASE_URL}/api/delivery-companies/${company.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...connectData,
          status: 'true'
        })
      });

      const result = await response.json();

      if (result.success) {
        setIsConnectModalOpen(false);
        setConnectData({ client_id: '', key_api: '', token_api: '' });
        fetchCompanies();
        toast.success('Successfully connected!');
      } else {
        throw new Error(result.message || 'Failed to connect');
      }
    } catch (error) {
      console.error('Error connecting company:', error);
      toast.error(error.message || 'Failed to connect. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 'true' : 'false') : value
    }));
  };

  const handleConnectInputChange = (e) => {
    const { name, value } = e.target;
    setConnectData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const maskHalf = (value) => {
    if (value === null || value === undefined) return null;
  
    const str = String(value); // ✅ force string
    const half = Math.ceil(str.length / 3.5);
  
    return str.slice(0, half) + '...';
  };

  return (
    <>
      <div
        key={company.id}
        className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group hover:-translate-y-1"
      >
        {/* Card Header with Logo */}
        <div className="relative bg-gradient-to-br from-[var(--color-primary-50)] to-[var(--color-primary-50)] p-6 flex items-center justify-center">
          <div className="w-20 h-20 rounded-full bg-white shadow-md flex items-center justify-center overflow-hidden">
            <img
              src={company.logo || "/placeholder.svg"}
              alt={company.name}
              className="w-16 h-16 object-contain"
              width={16}
              height={16}
            />
          </div>

          {/* Status Badge */}
          <div className="absolute top-4 right-4">
            <span
              className={`flex items-center gap-1 px-3 pt-0.5 pb-0.5 rounded-full text-[11px] font-medium ${
                company.status === "true" ? "bg-green-200 text-green-700" : "bg-red-100 text-red-600"
              }`}
            >
              <div className={`w-2 h-2 rounded-full ${company.status === "true" ? "bg-green-500 " : "bg-red-500"}`}></div>
              {company.status === "true" ? "Active" : "Désactive"}
            </span>
          </div>
        </div>

        {/* Card Body */}
        <div className="p-4">
          <h3 className="text-lg font-bold text-gray-800 mb-4 text-center">{company.name}</h3>

          <div className="space-y-3 mb-4">
            <div className="flex items-center text-xs">
              <span className="text-gray-500 w-20">Client ID:</span>
              <span className="font-medium text-gray-700">
                {company.client_id ? company.client_id : <span className="text-gray-400 italic font-normal">Aucune</span>}
              </span>
            </div>

            <div className="flex items-start text-xs">
              <span className="text-gray-500 w-20 flex-shrink-0">Token:</span>
              <span className="font-mono text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded break-all">
                {company.token_api ? maskHalf(company.token_api) : <span className="text-gray-400 italic font-normal">Aucune</span>}
              </span>
            </div>

            <div className="flex items-start text-xs">
              <span className="text-gray-500 w-20 flex-shrink-0">Key:</span>
              <span className="font-mono text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded break-all">
                {company.key_api ? maskHalf(company.key_api) : <span className="text-gray-400 italic font-normal">Aucune</span>}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          {company.token_api  ? (
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setIsEditModalOpen(true)}
                className="p-2 cursor-pointer border-2 border-gray-200 hover:border-[var(--color-primary-500)] text-gray-600 hover:text-[var(--color-primary-500)] rounded-xl transition-all duration-300"
                disabled={isLoading}
              >
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </button>
            </div>
          ) : (
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setIsConnectModalOpen(true)}
                className="flex items-center gap-1 cursor-pointer text-xs py-1.5 px-3 bg-green-700 text-white rounded-md shadow-sm hover:bg-green-800 transition-colors duration-300"
                disabled={isLoading}
              >
                <Link className="w-3.5 h-3.5 text-white" />
                Connecter
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-[99] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-bold text-gray-800">Modifier les informations {company.name}</h2>
                <button
                  onClick={() => setIsEditModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl cursor-pointer"
                  disabled={isLoading}
                >
                  &times;
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                    <img
                      src={company.logo || "/placeholder.svg"}
                      alt={company.name}
                      className="w-12 h-12 object-contain"
                      width={12}
                      height={12}
                    />
                  </div>
                  <p className="text-gray-600 text-[11px]">Modifier les informations pour {company.name}</p>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Client ID</label>
                  <input
                    name="client_id"
                    type="text"
                    value={formData.client_id}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-primary-500)] focus:border-transparent"
                    disabled={isLoading}
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">API Token</label>
                  <input
                    name="token_api"
                    type="text"
                    value={formData.token_api}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-primary-500)] focus:border-transparent"
                    disabled={isLoading}
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">API Key</label>
                  <input
                    name="key_api"
                    type="text"
                    value={formData.key_api}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-primary-500)] focus:border-transparent"
                    disabled={isLoading}
                  />
                </div>
                
                <div className="flex items-center mt-3">
                  <input
                    name="status"
                    type="checkbox"
                    id="status"
                    checked={formData.status === 'true'}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-[var(--color-primary-500)] bg-gray-100 border-gray-300 rounded focus:ring-[var(--color-primary-500)] focus:ring-2 cursor-pointer"
                    disabled={isLoading}
                  />
                  <label htmlFor="status" className="ml-2 text-xs text-gray-700 cursor-pointer">Active Status</label>
                </div>
                
                <div className="text-[11px] text-gray-500 mt-4">
                  <p>Modifiez les informations de l'entreprise selon vos besoins. Tous les champs sont obligatoires pour une intégration correcte.</p>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={() => setIsEditModalOpen(false)}
                  className="py-1.5 px-3 text-xs cursor-pointer text-gray-600 bg-gray-200 hover:text-gray-800 rounded-md shadow-sm font-medium"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditSubmit}
                  disabled={isLoading}
                  className="flex items-center gap-1 cursor-pointer text-xs py-1.5 px-3 bg-[var(--color-primary-400)] text-white rounded-md shadow-sm hover:bg-[var(--color-primary-500)] transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin h-3.5 w-3.5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    <>
                      <svg 
                        className="w-3.5 h-3.5 text-white" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                        />
                      </svg>
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Connect Modal */}
      {isConnectModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-[99] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-bold text-gray-800">Connect to {company.name}</h2>
                <button
                  onClick={() => setIsConnectModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl cursor-pointer"
                  disabled={isLoading}
                >
                  &times;
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                    <img
                      src={company.logo || "/placeholder.svg"}
                      alt={company.name}
                      className="w-12 h-12 object-contain"
                      width={12}
                      height={12}
                    />
                  </div>
                  <p className="text-gray-600 text-[11px]">Enter credentials to connect with {company.name}</p>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">API Client ID</label>
                  <input
                    name="client_id"
                    type="text"
                    value={connectData.client_id}
                    onChange={handleConnectInputChange}
                    placeholder="Enter client ID"
                    className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
                    disabled={isLoading}
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">API Key</label>
                  <input
                    name="key_api"
                    type="password"
                    value={connectData.key_api}
                    onChange={handleConnectInputChange}
                    placeholder="Enter API key"
                    className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
                    disabled={isLoading}
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">API Secret/Token</label>
                  <input
                    name="token_api"
                    type="password"
                    value={connectData.token_api}
                    onChange={handleConnectInputChange}
                    placeholder="Enter API token"
                    className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
                    disabled={isLoading}
                  />
                </div>
                
                <div className="text-[11px] text-gray-500 mt-4">
                  <p>Vous devrez fournir des identifiants API valides fournis par {company.name} pour établir la connexion.</p>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={() => setIsConnectModalOpen(false)}
                  className="py-1.5 px-3 text-xs cursor-pointer text-gray-600 bg-gray-200 hover:text-gray-800 rounded-md shadow-sm font-medium"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConnectSubmit}
                  disabled={isLoading}
                  className="flex items-center gap-1 cursor-pointer text-xs py-1.5 px-3 bg-green-700 text-white rounded-md shadow-sm hover:bg-green-800 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin h-3.5 w-3.5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Connecting...
                    </>
                  ) : (
                    <>
                      <Link className="w-3.5 h-3.5 text-white" />
                      Connecter
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}